import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Tabs,
  Tab,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Timeline as TimelineIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Percent as PercentIcon,
  Build as BuildIcon,
  Comment as CommentIcon,
  Person as PersonIcon,
  DateRange as DateRangeIcon,
  LocationOn as LocationOnIcon
} from "@mui/icons-material";
import { format } from "date-fns";
import { GetDataFromApiWithParams } from "../../../../../services/api/ExecuteApiRequests"

// Helper function to safely parse number
const safeParseNumber = (value) => {
  if (!value && value !== 0) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleanValue = value.replace(/,/g, '').trim();
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// Format number with commas
const formatNumber = (number) => {
  if (!number && number !== 0) return "0";
  return new Intl.NumberFormat('en-US').format(number);
};

// Format percentage
const formatPercent = (value) => {
  if (!value && value !== 0) return "0%";
  return `${Math.round(value)}%`;
};

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`occupancy-tabpanel-${index}`}
      aria-labelledby={`occupancy-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Bar Chart Component
const BarChart = ({ title, data, dataKey, color, formatValue }) => {
  const items = Object.keys(data);
  
  if (items.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(data).map(d => d[dataKey] || 0), 0);
  
  if (maxValue === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
      </Box>
    );
  }
  
  const barWidth = Math.min(50, Math.max(30, 80 - items.length * 2));
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        gap: 2.5, 
        minHeight: 400, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        padding: 2
      }}>
        {items.map((item) => {
          const value = data[item][dataKey] || 0;
          const height = maxValue > 0 ? (value / maxValue) * 280 : 0;
          const percentage = maxValue > 0 ? ((value / maxValue) * 100).toFixed(1) : 0;
          
          return (
            <Tooltip 
              key={item} 
              title={`${item}: ${formatValue(value)} (${percentage}% of max)`} 
              arrow 
              placement="top"
            >
              <Box sx={{ 
                textAlign: 'center', 
                minWidth: 70,
                maxWidth: 120,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}>
                <Box sx={{ 
                  position: 'relative',
                  height: height,
                  width: `${barWidth}px`,
                  bgcolor: color,
                  borderRadius: '4px 4px 0 0',
                  margin: '0 auto',
                  transition: 'all 0.3s ease',
                  opacity: 0.85,
                  '&:hover': {
                    opacity: 1,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                  }
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      position: 'absolute', 
                      top: -22, 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: color,
                      whiteSpace: 'nowrap',
                      display: height > 35 ? 'none' : 'block'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                </Box>
                
                {height > 35 && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: 'white',
                      mt: -2.5,
                      mb: 0.5,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                )}
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1.5, 
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    maxWidth: '100px',
                    wordWrap: 'break-word',
                    lineHeight: 1.2,
                    color: '#333'
                  }}
                >
                  {item.length > 20 ? item.substring(0, 18) + '...' : item}
                </Typography>
                
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    color: '#888',
                    fontSize: '0.65rem',
                    mt: 0.25
                  }}
                >
                  ({percentage}%)
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      <Box sx={{ 
        width: '100%', 
        bgcolor: '#f8f9fa', 
        mt: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        p: 2, 
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
          Total {title}: {formatValue(Object.values(data).reduce((sum, d) => sum + (d[dataKey] || 0), 0))}
        </Typography>
      </Box>
    </Box>
  );
};

// Pie Chart Component
const PieChart = ({ title, data, formatValue }) => {
  const items = Object.keys(data);
  const total = items.reduce((sum, key) => sum + (data[key] || 0), 0);
  const colors = ['#4caf50', '#ff9800', '#e91e63', '#2196f3', '#9c27b0', '#d32f2f', '#0288d1', '#7b1fa2', '#e65100', '#c2185b', '#00695c', '#f44336'];
  
  if (items.length === 0 || total === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for the selected filters</Typography>
      </Box>
    );
  }
  
  let startAngle = 0;
  const centerX = 120, centerY = 120, radius = 90;
  const svgSize = 280;
  
  const getPercentage = (value) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : 0;
  };
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 2, textAlign: 'center' }}>
        {title} - Total: {formatValue(total)}
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', width: svgSize, height: svgSize }}>
          <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
            {items.map((key, index) => {
              const value = data[key] || 0;
              const percentage = total > 0 ? (value / total) * 100 : 0;
              const angle = (percentage / 100) * 360;
              const endAngle = startAngle + angle;
              
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              
              const x1 = centerX + radius * Math.cos(startRad);
              const y1 = centerY + radius * Math.sin(startRad);
              const x2 = centerX + radius * Math.cos(endRad);
              const y2 = centerY + radius * Math.sin(endRad);
              
              const largeArcFlag = angle > 180 ? 1 : 0;
              const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
              
              const slice = (
                <path 
                  key={key} 
                  d={pathData} 
                  fill={colors[index % colors.length]} 
                  stroke="white" 
                  strokeWidth="2"
                  style={{ cursor: 'pointer', transition: 'opacity 0.3s' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                />
              );
              startAngle = endAngle;
              return slice;
            })}
            
            <circle cx={centerX} cy={centerY} r="25" fill="white" />
            <text x={centerX} y={centerY - 5} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#333">Total</text>
            <text x={centerX} y={centerY + 12} textAnchor="middle" fontSize="12" fontWeight="bold" fill="#1976d2">{formatValue(total)}</text>
          </svg>
        </Box>
        
        <Box sx={{ minWidth: 200, maxHeight: 250, overflowY: 'auto' }}>
          {items.map((key, index) => {
            const value = data[key] || 0;
            const percentage = getPercentage(value);
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: colors[index % colors.length], borderRadius: 1 }} />
                <Typography variant="caption" sx={{ flex: 1 }}>
                  {key.length > 20 ? key.substring(0, 18) + '...' : key} ({percentage}%)
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ color: colors[index % colors.length] }}>
                  {formatValue(value)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      
      <Box sx={{ width: '100%', bgcolor: '#f5f5f5', mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">Total Items: {items.length}</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>Total Value: {formatValue(total)}</Typography>
      </Box>
    </Box>
  );
};

// Yearly Trend Line Chart
const YearlyTrendLineChart = ({ title, yearlyData, dataKeys, colors, formatValue }) => {
  const years = Object.keys(yearlyData).sort((a, b) => a - b);
  
  if (years.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No yearly data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(yearlyData).flatMap(d => 
    dataKeys.map(key => d[key] || 0)
  ), 0);
  
  const chartHeight = 350;
  const chartWidth = Math.max(600, years.length * 100);
  const padding = { top: 30, right: 40, bottom: 60, left: 80 };
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>
      
      <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
        {[0, 25, 50, 75, 100].map((percent) => {
          const y = chartHeight - padding.bottom - (percent / 100) * (chartHeight - padding.top - padding.bottom);
          const value = (maxValue * percent) / 100;
          return (
            <g key={percent}>
              <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} stroke="#e0e0e0" strokeWidth="1" strokeDasharray="4" />
              <text x={padding.left - 8} y={y} textAnchor="end" fontSize="10" fill="#666">{formatValue(value)}</text>
            </g>
          );
        })}
        
        {dataKeys.map((dataKey, idx) => (
          <polyline
            key={dataKey}
            points={years.map((year, index) => {
              const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
              const value = yearlyData[year]?.[dataKey] || 0;
              const y = chartHeight - padding.bottom - (value / maxValue) * (chartHeight - padding.top - padding.bottom);
              return `${x},${y}`;
            }).join(' ')}
            fill="none"
            stroke={colors[idx % colors.length]}
            strokeWidth="2.5"
          />
        ))}
        
        {years.map((year, index) => {
          const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
          
          return (
            <g key={year}>
              {dataKeys.map((dataKey, idx) => {
                const value = yearlyData[year]?.[dataKey] || 0;
                const y = chartHeight - padding.bottom - (value / maxValue) * (chartHeight - padding.top - padding.bottom);
                const color = colors[idx % colors.length];
                
                return (
                  <g key={dataKey}>
                    <circle cx={x} cy={y} r="5" fill={color} stroke="white" strokeWidth="2" style={{ cursor: 'pointer' }} />
                    {value > 0 && (
                      <text 
                        x={x} 
                        y={idx === 0 ? y - 10 : y + 15} 
                        textAnchor="middle" 
                        fontSize="8" 
                        fill={color}
                      >
                        {formatValue(value)}
                      </text>
                    )}
                  </g>
                );
              })}
              <text x={x} y={chartHeight - padding.bottom + 20} textAnchor="middle" fontSize="11" fontWeight="bold">{year}</text>
            </g>
          );
        })}
        
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={chartHeight - padding.bottom} stroke="#999" strokeWidth="1.5" />
        <line x1={padding.left} y1={chartHeight - padding.bottom} x2={chartWidth - padding.right} y2={chartHeight - padding.bottom} stroke="#999" strokeWidth="1.5" />
        
        <text x={20} y={chartHeight / 2} textAnchor="middle" fontSize="11" fill="#666" transform={`rotate(-90, 20, ${chartHeight / 2})`}>Number of Requests</text>
        <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="11" fill="#666">Year</text>
      </svg>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2, flexWrap: 'wrap' }}>
        {dataKeys.map((dataKey, idx) => {
          let label = "";
          if (dataKey === "totalRequests") label = "Total Requests";
          else if (dataKey === "totalApproved") label = "Approved";
          else if (dataKey === "totalPending") label = "Pending";
          else if (dataKey === "totalRejected") label = "Rejected";
          else if (dataKey === "avgCompletionRate") label = "Avg Completion Rate";
          else label = dataKey;
          
          return (
            <Box key={dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 3, bgcolor: colors[idx % colors.length], borderRadius: 1 }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{label}</Typography>
            </Box>
          );
        })}
      </Box>
      
      <Box sx={{ width: '100%', bgcolor: '#f5f5f5', mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">Years: {years.join(' → ')}</Typography>
      </Box>
    </Box>
  );
};

// Main Occupancy Permit Report Component
export default function OccupancyPermitReport() {
  // State Management
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPark, setSelectedPark] = useState("all");
  const [startYear, setStartYear] = useState("all");
  const [endYear, setEndYear] = useState("all");
  const [startMonth, setStartMonth] = useState("all");
  const [endMonth, setEndMonth] = useState("all");
  const [minCompletionPercent, setMinCompletionPercent] = useState("");
  const [maxCompletionPercent, setMaxCompletionPercent] = useState("");
  
  // Chart States
  const [showChart, setShowChart] = useState(false);
  const [chartView, setChartView] = useState("overview");
  const [chartTab, setChartTab] = useState(0);
  const [chartType, setChartType] = useState('bar');
  
  // Data States - using exact table column names
  const [occupancyPermits, setOccupancyPermits] = useState([]);
  const [occupancyFollowUps, setOccupancyFollowUps] = useState([]);
  const [filteredPermits, setFilteredPermits] = useState([]);
  const [filteredFollowUps, setFilteredFollowUps] = useState([]);
  
  // Dynamic filter options
  const [statusesList, setStatusesList] = useState(["all"]);
  const [parksList, setParksList] = useState(["all"]);
  
  // Dialog States
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [openFollowUpDialog, setOpenFollowUpDialog] = useState(false);
  const [selectedFollowUps, setSelectedFollowUps] = useState([]);

  // Generate last 10 years dynamically
  const getLastNYears = (n) => {
    const currentYear = new Date().getFullYear();
    const years = ["all"];
    for (let i = 0; i < n; i++) {
      years.push((currentYear - i).toString());
    }
    return years;
  };
  
  const years = getLastNYears(10);
  
  const months = [
    { value: "all", label: "All Months" },
    { value: "01", label: "January" }, { value: "02", label: "February" },
    { value: "03", label: "March" }, { value: "04", label: "April" },
    { value: "05", label: "May" }, { value: "06", label: "June" },
    { value: "07", label: "July" }, { value: "08", label: "August" },
    { value: "09", label: "September" }, { value: "10", label: "October" },
    { value: "11", label: "November" }, { value: "12", label: "December" }
  ];

  const getStatusColor = (status) => {
    const statusUpper = status?.toUpperCase();
    switch(statusUpper) {
      case 'APPROVED':
      case 'ISSUED':
        return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'REQUESTED':
      case 'PENDING':
      case 'UNDER_REVIEW':
        return { bg: '#fff3e0', color: '#ef6c00' };
      case 'REJECTED':
      case 'CANCELLED':
        return { bg: '#ffebee', color: '#c62828' };
      default: 
        return { bg: '#f5f5f5', color: '#616161' };
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    if (date === '1900-01-01') return "N/A";
    try {
      return format(new Date(date), 'dd/MM/yyyy');
    } catch {
      return "Invalid Date";
    }
  };

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [occupancyRes, followUpRes, parksRes] = await Promise.all([
          GetDataFromApiWithParams(`design-management/occupancy-permits1`),
          GetDataFromApiWithParams(`design-management/occupancy-permit-follow-up`),
          GetDataFromApiWithParams(`parks`)
        ]);
        console.log("OKOKOKOK", occupancyRes)
        let occupancy = [];
        let followUps = [];
        let parks = [];
        
        if (occupancyRes && Array.isArray(occupancyRes)) occupancy = occupancyRes;
        else if (occupancyRes?.data && Array.isArray(occupancyRes.data)) occupancy = occupancyRes.data;
        
        if (followUpRes && Array.isArray(followUpRes)) followUps = followUpRes;
        else if (followUpRes?.data && Array.isArray(followUpRes.data)) followUps = followUpRes.data;
        
        if (parksRes && Array.isArray(parksRes)) parks = parksRes;
        else if (parksRes?.data && Array.isArray(parksRes.data)) parks = parksRes.data;
        
        console.log("XXXXXXXXXXX", occupancy)
        const formattedOccupancy = occupancy.map(occ => {
          let year = "2024";
          let month = "01";
          if (occ.requested_on) {
            const date = new Date(occ.requested_on);
            year = date.getFullYear().toString();
            month = (date.getMonth() + 1).toString().padStart(2, '0');
          }
          
          // Find park name from parks list based on park_id
          const park = parks.find(p => p.id === occ.park_id);
          const parkName = park ? park.name : (occ.park_name || "N/A");
          
          // Return object with exact database column names
          return {
            id: occ.id,
            building_permit_id: occ.buildingpermitid,
            requested_on: occ.requested_on,
            requested_on_formatted: formatDate(occ.requested_on),
            note: occ.note || "N/A",
            attached_signed_form: occ.attached_signed_form,
            request_registered_by: occ.request_registered_by,
            approved_on: occ.approved_on,
            approved_on_formatted: formatDate(occ.approved_on),
            approved_by: occ.approved_by,
            comment: occ.comment,
            status: occ.status || "REQUESTED",
            description: occ.description || "N/A",
            year: year,
            month: month,
            park_id: occ.park_id || null,
            park_name: parkName
          };
        });
        
        // Format follow-ups with exact table column names
        const formattedFollowUps = followUps.map(fu => ({
          permit_id: fu.permit_id,
          followed_on: fu.followed_on,
          followed_on_formatted: formatDate(fu.followed_on),
          checked_by: fu.checked_by,
          comment: fu.comment,
          percent_completed: fu.percent_completed || 0
        }));
        
        console.log()
        setOccupancyPermits(formattedOccupancy);
        setFilteredPermits(formattedOccupancy);
        setOccupancyFollowUps(formattedFollowUps);
        setFilteredFollowUps(formattedFollowUps);
        
        const uniqueStatuses = new Set();
        formattedOccupancy.forEach(permit => {
          if (permit.status) {
            uniqueStatuses.add(permit.status);
          }
        });
        setStatusesList(["all", ...Array.from(uniqueStatuses).sort()]);
        
        // Extract unique park names from parks API response (like code 2)
        const uniqueParks = new Set();
        if (parks && Array.isArray(parks)) {
          parks.forEach(park => {
            if (park.name && park.name !== "N/A") {
              uniqueParks.add(park.name);
            }
          });
        }
        // Also add parks from occupancy data if any
        formattedOccupancy.forEach(permit => {
          if (permit.park_name && permit.park_name !== "N/A") {
            uniqueParks.add(permit.park_name);
          }
        });
        setParksList(["all", ...Array.from(uniqueParks).sort()]);
        
      } catch (error) {
        console.error("Failed to fetch occupancy permit data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalRequests = filteredPermits.length;
    const totalApproved = filteredPermits.filter(p => p.status === "Finished" || p.status === "approved").length;
    const totalPending = filteredPermits.filter(p => p.status === "REQUESTED" || p.status === "PENDING" || p.status === "UNDER_REVIEW").length;
    const totalRejected = filteredPermits.filter(p => p.status === "REJECTED" || p.status === "CANCELLED").length;
    const approvalRate = totalRequests > 0 ? (totalApproved / totalRequests) * 100 : 0;
    
    // Calculate follow-up statistics
    const totalFollowUps = filteredFollowUps.length;
    const averageCompletionRate = filteredFollowUps.length > 0 ? 
      filteredFollowUps.reduce((sum, f) => sum + (f.percent_completed || 0), 0) / filteredFollowUps.length : 0;
    
    const highCompletionCount = filteredFollowUps.filter(f => (f.percent_completed || 0) >= 75).length;
    const mediumCompletionCount = filteredFollowUps.filter(f => (f.percent_completed || 0) >= 50 && (f.percent_completed || 0) < 75).length;
    const lowCompletionCount = filteredFollowUps.filter(f => (f.percent_completed || 0) < 50).length;
    
    // Get unique permits with follow-ups
    const permitsWithFollowUps = new Set(filteredFollowUps.map(f => f.permit_id));
    
    return {
      totalRequests,
      totalApproved,
      totalPending,
      totalRejected,
      approvalRate,
      totalFollowUps,
      averageCompletionRate,
      highCompletionCount,
      mediumCompletionCount,
      lowCompletionCount,
      permitsWithFollowUpsCount: permitsWithFollowUps.size
    };
  }, [filteredPermits, filteredFollowUps]);

  // Chart data preparation

  console.log("ZZZZZZZZZZZ", filteredPermits.filter(p => p.status === "Finished" || p.status === "approved").length)
  const chartData = useMemo(() => {
    const statusDistribution = {
      "ISSUED": filteredPermits.filter(p => p.status === "Finished" || p.status === "approved").length,
      "REQUESTED": filteredPermits.filter(p => p.status === "REQUESTED" || p.status === "PENDING").length,
      "REJECTED": filteredPermits.filter(p => p.status === "REJECTED" || p.status === "CANCELLED").length
    };
    
    const monthlyRequests = {
      "Jan": 0, "Feb": 0, "Mar": 0, "Apr": 0, "May": 0, "Jun": 0,
      "Jul": 0, "Aug": 0, "Sep": 0, "Oct": 0, "Nov": 0, "Dec": 0
    };
    
    const monthlyApproved = {
      "Jan": 0, "Feb": 0, "Mar": 0, "Apr": 0, "May": 0, "Jun": 0,
      "Jul": 0, "Aug": 0, "Sep": 0, "Oct": 0, "Nov": 0, "Dec": 0
    };
    
    filteredPermits.forEach(permit => {
      const monthNum = permit.month;
      const monthNames = { "01":"Jan","02":"Feb","03":"Mar","04":"Apr","05":"May","06":"Jun",
                          "07":"Jul","08":"Aug","09":"Sep","10":"Oct","11":"Nov","12":"Dec" };
      const monthName = monthNames[monthNum] || "Jan";
      monthlyRequests[monthName] = (monthlyRequests[monthName] || 0) + 1;
      
      if (permit.status === "Finished" || permit.status === "approved") {
        monthlyApproved[monthName] = (monthlyApproved[monthName] || 0) + 1;
      }
    });
    
    const yearlyData = {};
    filteredPermits.forEach(permit => {
      const year = permit.year;
      if (!yearlyData[year]) {
        yearlyData[year] = { totalRequests: 0, totalApproved: 0, totalPending: 0, totalRejected: 0 };
      }
      yearlyData[year].totalRequests++;
      if (permit.status === "Finished" || permit.status === "approved") {
        yearlyData[year].totalApproved++;
      }
      if (permit.status === "REQUESTED" || permit.status === "PENDING") {
        yearlyData[year].totalPending++;
      }
      if (permit.status === "REJECTED" || permit.status === "CANCELLED") {
        yearlyData[year].totalRejected++;
      }
    });
    
    // Add average completion rate to yearly data
    filteredFollowUps.forEach(fu => {
      const permit = filteredPermits.find(p => p.id === fu.permit_id);
      if (permit) {
        const year = permit.year;
        if (!yearlyData[year]) {
          yearlyData[year] = { totalRequests: 0, totalApproved: 0, totalPending: 0, totalRejected: 0, totalCompletion: 0, completionCount: 0 };
        }
        yearlyData[year].totalCompletion = (yearlyData[year].totalCompletion || 0) + (fu.percent_completed || 0);
        yearlyData[year].completionCount = (yearlyData[year].completionCount || 0) + 1;
      }
    });
    
    // Calculate average completion rate per year
    Object.keys(yearlyData).forEach(year => {
      if (yearlyData[year].completionCount > 0) {
        yearlyData[year].avgCompletionRate = yearlyData[year].totalCompletion / yearlyData[year].completionCount;
      } else {
        yearlyData[year].avgCompletionRate = 0;
      }
    });
    
    const completionDistribution = {
      "High (≥75%)": summaryStats.highCompletionCount,
      "Medium (50-74%)": summaryStats.mediumCompletionCount,
      "Low (<50%)": summaryStats.lowCompletionCount
    };
    
    return { 
      statusDistribution, 
      monthlyRequests, 
      monthlyApproved, 
      yearlyData, 
      completionDistribution
    };
  }, [filteredPermits, filteredFollowUps, summaryStats]);

  // Filter Data
  const applyFilters = useCallback(() => {
    let filteredList = [...occupancyPermits];
    let filteredFollowUpsList = [...occupancyFollowUps];
    
    if (searchTerm) {
      filteredList = filteredList.filter(item =>
        item.id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.building_permit_id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.note?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.request_registered_by?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.approved_by?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedStatus !== "all") {
      filteredList = filteredList.filter(item => 
        item.status?.toUpperCase() === selectedStatus.toUpperCase()
      );
    }
    
    if (selectedPark !== "all") {
      filteredList = filteredList.filter(item => 
        item.park_name === selectedPark
      );
    }
    
    if (startYear !== "all" || endYear !== "all") {
      filteredList = filteredList.filter(item => {
        const itemYear = parseInt(item.year);
        const startYearNum = startYear !== "all" ? parseInt(startYear) : -Infinity;
        const endYearNum = endYear !== "all" ? parseInt(endYear) : Infinity;
        return itemYear >= startYearNum && itemYear <= endYearNum;
      });
    }
    
    if (startMonth !== "all" || endMonth !== "all") {
      filteredList = filteredList.filter(item => {
        const itemMonth = parseInt(item.month);
        const startMonthNum = startMonth !== "all" ? parseInt(startMonth) : 1;
        const endMonthNum = endMonth !== "all" ? parseInt(endMonth) : 12;
        return itemMonth >= startMonthNum && itemMonth <= endMonthNum;
      });
    }
    
    // Filter follow-ups based on permit IDs in filtered list
    const permitIds = new Set(filteredList.map(p => p.id));
    filteredFollowUpsList = occupancyFollowUps.filter(fu => permitIds.has(fu.permit_id));
    
    // Apply completion percentage filters
    if (minCompletionPercent !== "") {
      const minVal = parseFloat(minCompletionPercent);
      if (!isNaN(minVal)) {
        filteredFollowUpsList = filteredFollowUpsList.filter(fu => (fu.percent_completed || 0) >= minVal);
      }
    }
    
    if (maxCompletionPercent !== "") {
      const maxVal = parseFloat(maxCompletionPercent);
      if (!isNaN(maxVal)) {
        filteredFollowUpsList = filteredFollowUpsList.filter(fu => (fu.percent_completed || 0) <= maxVal);
      }
    }
    
    setFilteredPermits(filteredList);
    setFilteredFollowUps(filteredFollowUpsList);
    setPage(0);
  }, [occupancyPermits, occupancyFollowUps, searchTerm, selectedStatus, selectedPark, startYear, endYear, startMonth, endMonth, minCompletionPercent, maxCompletionPercent]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Export to CSV - using exact column names
  const handleExportCSV = () => {
    const headers = ['id', 'building_permit_id', 'requested_on', 'note', 'attached_signed_form', 'request_registered_by', 'approved_on', 'approved_by', 'comment', 'status', 'description', 'park_name'];
    const data = filteredPermits.map(item => [
      item.id,
      item.building_permit_id,
      item.requested_on_formatted,
      item.note,
      item.attached_signed_form || 'N/A',
      item.request_registered_by || 'N/A',
      item.approved_on_formatted,
      item.approved_by || 'N/A',
      item.comment || 'N/A',
      item.status,
      item.description,
      item.park_name || 'N/A'
    ]);
    
    const csvContent = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `occupancy_permits_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  // Export Follow-ups to CSV - using exact column names
  const handleExportFollowUpsCSV = () => {
    const headers = ['permit_id', 'followed_on', 'checked_by', 'comment', 'percent_completed'];
    const data = filteredFollowUps.map(item => [
      item.permit_id,
      item.followed_on_formatted,
      item.checked_by || 'N/A',
      item.comment || 'N/A',
      item.percent_completed
    ]);
    
    const csvContent = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `occupancy_followups_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedStatus("all");
    setSelectedPark("all");
    setStartYear("all");
    setEndYear("all");
    setStartMonth("all");
    setEndMonth("all");
    setMinCompletionPercent("");
    setMaxCompletionPercent("");
  };

  const handleChangePage = (event, newPage) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
  const getCurrentPageData = () => { return filteredPermits.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage); };
  
  const handleViewDetails = (permit) => { 
    setSelectedPermit(permit); 
    setOpenDetailsDialog(true); 
  };
  
  const handleViewFollowUps = (permitId) => {
    const followUps = occupancyFollowUps.filter(fu => fu.permit_id === permitId);
    setSelectedFollowUps(followUps);
    setOpenFollowUpDialog(true);
  };
  
  const getFollowUpsForPermit = (permitId) => {
    return occupancyFollowUps.filter(fu => fu.permit_id === permitId);
  };
  
  const getAverageCompletionForPermit = (permitId) => {
    const followUps = getFollowUpsForPermit(permitId);
    if (followUps.length === 0) return null;
    const avg = followUps.reduce((sum, f) => sum + (f.percent_completed || 0), 0) / followUps.length;
    return Math.round(avg);
  };

  const renderChart = (title, data, dataKey, color, formatValue) => {
    const hasValidData = Object.values(data).some(item => {
      const value = item[dataKey];
      return value && value > 0;
    });
    
    if (!hasValidData) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
        </Box>
      );
    }
    
    if (chartType === 'pie') {
      const pieData = {};
      Object.entries(data).forEach(([key, val]) => { 
        const value = val[dataKey] || 0;
        if (value > 0) {
          pieData[key] = value;
        }
      });
      
      if (Object.keys(pieData).length === 0) {
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
          </Box>
        );
      }
      return <PieChart title={title} data={pieData} formatValue={formatValue} />;
    } else {
      return <BarChart title={title} data={data} dataKey={dataKey} color={color} formatValue={formatValue} />;
    }
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#f5f5f5', minHeight: '100vh', p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#1a237e', fontWeight: 'bold', textAlign: 'center', mb: 1 }}>
        Occupancy Permit Report
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 3 }}>
        Track and analyze occupancy permit requests, approvals, and follow-up inspections
      </Typography>
      
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px', p: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 0.25, px: 0.5, '&:last-child': { pb: 0.25 } }}>
              <AssignmentIcon sx={{ color: '#1976d2', fontSize: 14, mb: 0 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1.2 }}>
                {formatNumber(summaryStats.totalRequests)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.6rem', lineHeight: 1.1 }}>Total Requests</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px', p: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 0.25, px: 0.5, '&:last-child': { pb: 0.25 } }}>
              <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 14, mb: 0 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1.2 }}>
                {formatNumber(summaryStats.totalApproved)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.6rem', lineHeight: 1.1 }}>Approved</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px', p: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 0.25, px: 0.5, '&:last-child': { pb: 0.25 } }}>
              <PendingIcon sx={{ color: '#ed6c02', fontSize: 14, mb: 0 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1.2 }}>
                {formatNumber(summaryStats.totalPending)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.6rem', lineHeight: 1.1 }}>Pending</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '200px', p: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 0.25, px: 0.5, '&:last-child': { pb: 0.25 } }}>
              <PercentIcon sx={{ color: '#9c27b0', fontSize: 14, mb: 0 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1.2 }}>
                {formatPercent(summaryStats.approvalRate)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.6rem', lineHeight: 1.1 }}>Approval Rate</Typography>
            </CardContent>
          </Card>
        </Grid>
        
       
      </Grid>
      
      {/* Filters Section */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={2.4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by id, building_permit_id, note..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
                endAdornment: searchTerm && (<InputAdornment position="end"><ClearIcon sx={{ cursor: 'pointer', fontSize: 'small' }} onClick={() => setSearchTerm("")} /></InputAdornment>)
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={1.8}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} label="Status">
                {statusesList.map((status) => (<MenuItem key={status} value={status}>{status === "all" ? "All Status" : status}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={1.8}>
            <FormControl fullWidth size="small">
              <InputLabel>Park</InputLabel>
              <Select value={selectedPark} onChange={(e) => setSelectedPark(e.target.value)} label="Park">
                {parksList.map((park) => (<MenuItem key={park} value={park}>{park === "all" ? "All Parks" : park}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Year From</InputLabel>
              <Select value={startYear} onChange={(e) => setStartYear(e.target.value)} label="Year From">
                {years.map((year) => (<MenuItem key={year} value={year}>{year === "all" ? "All Years" : year}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Year To</InputLabel>
              <Select value={endYear} onChange={(e) => setEndYear(e.target.value)} label="Year To">
                {years.map((year) => (<MenuItem key={year} value={year}>{year === "all" ? "All Years" : year}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={resetFilters} startIcon={<ClearIcon />} size="small" sx={{ textTransform: 'none' }}>
              Reset Filters
            </Button>
          </Grid>
        </Grid>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Month From</InputLabel>
              <Select value={startMonth} onChange={(e) => setStartMonth(e.target.value)} label="Month From">
                {months.map((month) => (<MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Month To</InputLabel>
              <Select value={endMonth} onChange={(e) => setEndMonth(e.target.value)} label="Month To">
                {months.map((month) => (<MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
        
          
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="contained" color="primary" size="small" onClick={handleExportCSV} startIcon={<DownloadIcon />} sx={{ textTransform: 'none' }}>
              Export CSV
            </Button>
            {/* <Button variant="outlined" color="secondary" size="small" onClick={handleExportFollowUpsCSV} startIcon={<DownloadIcon />} sx={{ textTransform: 'none' }}>
              Export Follow-ups
            </Button> */}
            <Button variant="outlined" size="small" onClick={() => setShowChart(!showChart)} sx={{ textTransform: 'none' }}>
              {showChart ? "Hide Chart" : "Show Chart"}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Table with exact column names */}
      {!showChart && (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
         
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading occupancy permit data...</Typography>
            </Box>
          ) : filteredPermits.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <Typography sx={{ color: 'text.secondary' }}>No occupancy permit data available</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Park Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>building_permit_id</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>requested_on</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>approved_on</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>approved_by</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getCurrentPageData().map((row) => {
                      const statusColors = getStatusColor(row.status);
                      const avgCompletion = getAverageCompletionForPermit(row.id);
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.park_name}</TableCell>
                          <TableCell>{row.building_permit_id}</TableCell>
                          <TableCell>{row.requested_on_formatted}</TableCell>
                          <TableCell>
                          {row.status}
                           
                          </TableCell>
                          <TableCell>{row.approved_on_formatted}</TableCell>
                          <TableCell>{row.approved_by || 'N/A'}</TableCell>
                         
                          {/* <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="View Details">
                                <IconButton size="small" color="primary" onClick={() => handleViewDetails(row)}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View Follow-ups">
                                <IconButton size="small" color="secondary" onClick={() => handleViewFollowUps(row.id)}>
                                  <BuildIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell> */}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination 
                rowsPerPageOptions={[5, 10, 25, 50]} 
                component="div" 
                count={filteredPermits.length} 
                rowsPerPage={rowsPerPage} 
                page={page} 
                onPageChange={handleChangePage} 
                onRowsPerPageChange={handleChangeRowsPerPage} 
              />
            </>
          )}
        </Paper>
      )}
      
      {/* Charts Section */}
      {showChart && (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button variant={chartView === 'overview' ? 'contained' : 'outlined'} size="small" onClick={() => setChartView('overview')} sx={{ textTransform: 'none', minWidth: '120px' }}>
              <BarChartIcon sx={{ mr: 1, fontSize: 18 }} />Overview
            </Button>
            <Button variant={chartView === 'yearly' ? 'contained' : 'outlined'} size="small" onClick={() => setChartView('yearly')} sx={{ textTransform: 'none', minWidth: '120px' }}>
              <TimelineIcon sx={{ mr: 1, fontSize: 18 }} />Yearly Trend
            </Button>
          </Box>
          
          <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            {chartView === 'overview' ? (
              <>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary">Chart Type:</Typography>
                    <ToggleButtonGroup value={chartType} exclusive onChange={(e, val) => val && setChartType(val)} size="small">
                      <ToggleButton value="bar">
                        <Tooltip title="Bar Chart">
                          <BarChartIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value="pie">
                        <Tooltip title="Pie Chart">
                          <PieChartIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={chartTab} onChange={(e, newValue) => setChartTab(newValue)}>
                    <Tab label="Status Distribution" />
                    <Tab label="Monthly Requests" />
                    <Tab label="Monthly Approvals" />
                    <Tab label="Completion Distribution" />
                  </Tabs>
                </Box>
                
                <Box sx={{ mt: 2, minHeight: 450 }}>
                  <TabPanel value={chartTab} index={0}>
                    {renderChart("Status Distribution", 
                      Object.fromEntries(Object.entries(chartData.statusDistribution).map(([k, v]) => [k, { value: v }])), 
                      "value", "#1976d2", (v) => formatNumber(v))}
                  </TabPanel>
                  <TabPanel value={chartTab} index={1}>
                    {renderChart("Monthly Requests", 
                      Object.fromEntries(Object.entries(chartData.monthlyRequests).map(([k, v]) => [k, { value: v }])), 
                      "value", "#ff9800", (v) => formatNumber(v))}
                  </TabPanel>
                  <TabPanel value={chartTab} index={2}>
                    {renderChart("Monthly Approvals", 
                      Object.fromEntries(Object.entries(chartData.monthlyApproved).map(([k, v]) => [k, { value: v }])), 
                      "value", "#4caf50", (v) => formatNumber(v))}
                  </TabPanel>
                  <TabPanel value={chartTab} index={3}>
                    {renderChart("Completion Rate Distribution", 
                      Object.fromEntries(Object.entries(chartData.completionDistribution).map(([k, v]) => [k, { value: v }])), 
                      "value", "#9c27b0", (v) => formatNumber(v))}
                  </TabPanel>
                </Box>
              </>
            ) : (
              <YearlyTrendLineChart 
                title="Yearly Occupancy Permit Trends" 
                yearlyData={chartData.yearlyData} 
                dataKeys={['totalRequests', 'totalApproved', 'totalPending', 'avgCompletionRate']}
                colors={['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0']}
                formatValue={(v) => typeof v === 'number' && v < 1 ? formatPercent(v) : formatNumber(v)}
              />
            )}
          </Paper>
        </>
      )}
      
      {/* Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
              Occupancy Permit Details
            </Typography>
            <IconButton onClick={() => setOpenDetailsDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedPermit && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>id</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{selectedPermit.id}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>building_permit_id</Typography>
                  <Typography variant="body1">{selectedPermit.building_permit_id}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>requested_on</Typography>
                  <Typography variant="body1">{selectedPermit.requested_on_formatted}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>status</Typography>
                  <Chip label={selectedPermit.status} size="small" sx={getStatusColor(selectedPermit.status)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>request_registered_by</Typography>
                  <Typography variant="body1">{selectedPermit.request_registered_by || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>approved_on</Typography>
                  <Typography variant="body1">{selectedPermit.approved_on_formatted}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>approved_by</Typography>
                  <Typography variant="body1">{selectedPermit.approved_by || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>attached_signed_form</Typography>
                  <Typography variant="body1">{selectedPermit.attached_signed_form || 'N/A'}</Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 2 }}>Additional Information</Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>note</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f5f5f5', mt: 0.5 }}>
                    <Typography variant="body2">{selectedPermit.note || 'No notes'}</Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>description</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f5f5f5', mt: 0.5 }}>
                    <Typography variant="body2">{selectedPermit.description || 'No description'}</Typography>
                  </Paper>
                </Grid>
                
                {selectedPermit.comment && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ color: '#666' }}>comment</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#e3f2fd', mt: 0.5 }}>
                      <Typography variant="body2">{selectedPermit.comment}</Typography>
                    </Paper>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 2 }}>
                    occupancy_permit_follow_up
                  </Typography>
                  {getFollowUpsForPermit(selectedPermit.id).map((fu, idx) => (
                    <Paper key={idx} sx={{ p: 1.5, mb: 1, bgcolor: '#fafafa' }}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" fontWeight="bold">
                          <DateRangeIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                          followed_on: {fu.followed_on_formatted}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          <PersonIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                          checked_by: {fu.checked_by || 'Unknown'}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2} mb={0.5}>
                        <Chip 
                          label={formatPercent(fu.percent_completed)} 
                          size="small" 
                          sx={{ 
                            bgcolor: fu.percent_completed >= 75 ? '#e8f5e9' : fu.percent_completed >= 50 ? '#fff3e0' : '#ffebee',
                            color: fu.percent_completed >= 75 ? '#2e7d32' : fu.percent_completed >= 50 ? '#ef6c00' : '#c62828'
                          }} 
                        />
                      </Box>
                      {fu.comment && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <CommentIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle', color: '#666' }} />
                          comment: {fu.comment}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                  {getFollowUpsForPermit(selectedPermit.id).length === 0 && (
                    <Typography variant="body2" color="textSecondary">No follow-up inspections recorded</Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setOpenDetailsDialog(false)} variant="outlined">Close</Button>
        </Box>
      </Dialog>
      
      {/* Follow-ups Dialog */}
      <Dialog open={openFollowUpDialog} onClose={() => setOpenFollowUpDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
              occupancy_permit_follow_up
            </Typography>
            <IconButton onClick={() => setOpenFollowUpDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedFollowUps.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>followed_on</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>checked_by</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>percent_completed</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>comment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedFollowUps.map((fu, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{fu.followed_on_formatted}</TableCell>
                        <TableCell>{fu.checked_by || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={formatPercent(fu.percent_completed)} 
                            size="small" 
                            sx={{ 
                              bgcolor: fu.percent_completed >= 75 ? '#e8f5e9' : fu.percent_completed >= 50 ? '#fff3e0' : '#ffebee',
                              color: fu.percent_completed >= 75 ? '#2e7d32' : fu.percent_completed >= 50 ? '#ef6c00' : '#c62828'
                            }} 
                          />
                        </TableCell>
                        <TableCell>{fu.comment || 'No comment'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">No follow-up inspections recorded for this permit</Typography>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setOpenFollowUpDialog(false)} variant="outlined">Close</Button>
        </Box>
      </Dialog>
    </Box>
  );
}